import { CardSkeleton } from "@/components/atoms/CardSkeleton/CardSkeleton";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useOrgRole } from "@/hooks/useOrgRole";
import { ensureTimestamp } from "@/lib/utils";
import { isWordPressConnected } from "@/schemas";
import { useStore } from "@/store";
import { Loader2 } from "lucide-react";

const SYNC_ACTION_KEY = "sync";

const formatSyncedAt = (
  timestamp: unknown,
): string => {
  const date = ensureTimestamp(timestamp)?.toDate();
  if (!date) return "Never";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/**
 * WordPress status for the current domain, plus the only manual re-sync in the
 * app.
 *
 * Connecting WordPress stays in the setup wizard: it needs an application
 * password typed in, and duplicating that form here would be a second place to
 * maintain the credential flow. This section is for a domain that is already
 * connected -- see what the last sync did, and run another.
 *
 * "Sync now" is also how a domain connected before the cron migration gets its
 * `wp-sync` scheduled job: core arms that default job on every *successful*
 * sync (wordpress.py), so one press here is the arming event for a domain whose
 * only previous sync predates the feature.
 */
export const WordPressSection = () => {
  const currentDomain = useStore((state) => state.currentDomain);
  const wpIntegration = useStore((state) => state.wpIntegration);
  const isLoadingWpIntegration = useStore(
    (state) => state.isLoadingWpIntegration,
  );
  const wpIntegrationError = useStore((state) => state.wpIntegrationError);
  const wpActionLoading = useStore((state) => state.wpActionLoading);
  const wpActionError = useStore((state) => state.wpActionError);
  const triggerSync = useStore((state) => state.triggerSync);
  const { canEdit } = useOrgRole();

  const isSyncing = wpActionLoading[SYNC_ACTION_KEY] === true;
  // A document can exist while still missing site_url/connectedBy: core's sync
  // error path creates it with a merge-write before the integration is ever
  // initialised (see PartialWordPressDomainIntegrationSchema). That is a broken
  // connection, not an absent one, and it must stay visible -- the slice already
  // phrases that case as wpIntegrationError, so don't restate it here.
  const isConnected = isWordPressConnected(wpIntegration);
  // The last sync's own failure, kept on the document; distinct from
  // wpActionError, which only describes the press the member just made. Only
  // shown for a healthy connection: on a broken one wpIntegrationError is
  // already carrying the same error_message.
  const lastSyncError =
    isConnected && wpIntegration?.sync_status === "error"
      ? (wpIntegration.error_message ?? "The last sync failed")
      : null;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-1">
          <h3 className="text-lg font-medium">WordPress</h3>
        </div>
      </div>

      {isLoadingWpIntegration && wpIntegration === null ? (
        <CardSkeleton />
      ) : wpIntegration === null ? (
        <p className="text-sm text-muted-foreground">
          No WordPress connection configured yet
        </p>
      ) : (
        <Card className="overflow-hidden py-3 relative">
          <div className="flex items-stretch gap-3 px-3 md:px-4">
            <div className="flex-1 min-w-0 space-y-3">
              <div className="space-y-1">
                <h4 className="text-base font-medium">Site</h4>
                <p className="text-sm text-muted-foreground break-all">
                  {wpIntegration.site_url || currentDomain?.url || "—"}
                </p>
              </div>
              <div className="space-y-1">
                <h4 className="text-base font-medium">Last sync</h4>
                <p className="text-sm text-muted-foreground">
                  {formatSyncedAt(wpIntegration.last_sync_at)}
                  {wpIntegration.total_items_synced > 0 &&
                    ` · ${wpIntegration.total_items_synced} items`}
                </p>
              </div>
              {wpIntegrationError && (
                <p className="text-sm text-destructive break-words">
                  {wpIntegrationError}
                </p>
              )}
              {lastSyncError && (
                <p className="text-sm text-destructive break-words">
                  {lastSyncError}
                </p>
              )}
              {wpActionError && (
                <p className="text-sm text-destructive break-words">
                  {wpActionError}
                </p>
              )}
            </div>

            <div className="w-px bg-gray-200 self-stretch" />

            <div className="flex flex-col gap-2 flex-shrink-0 w-24">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  if (currentDomain?.id) void triggerSync(currentDomain.id);
                }}
                disabled={
                  !canEdit ||
                  isSyncing ||
                  !wpIntegration.site_id ||
                  !currentDomain?.id
                }
                title="Pull the latest posts and pages from WordPress"
              >
                {isSyncing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Sync now"
                )}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
